import { createPortal } from 'react-dom';
import { useAnchoredMenu } from './useAnchoredMenu';
import { useEffect, useState, type ReactNode, type Ref } from 'react';
import type { ScriptFeatures } from '../../shared/types';

interface Props {
  inline?: boolean;
  current: ScriptFeatures;
  onChange: (next: ScriptFeatures) => Promise<void> | void;
  onRotateSlug?: () => Promise<void> | void;
  onOpenVersionHistory?: () => void;
  /** Supply the trigger to place this popover in another control, e.g. the
   *  editor's icon rail. Attach the supplied ref to its anchor button. */
  trigger?: (args: { open: boolean; toggle: () => void; ref: Ref<HTMLButtonElement> }) => ReactNode;
}

export default function FeaturesPanel({
  current,
  onChange,
  onRotateSlug,
  onOpenVersionHistory,
  trigger,
  inline = false,
}: Props) {
  const menu = useAnchoredMenu({ width: 260, itemSelector: 'button' });
  const [busy, setBusy] = useState(false);
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  // The panel outlives its popup, so an armed rotate confirm must not be
  // waiting for whoever opens Features next.
  useEffect(() => {
    if (!menu.open) setConfirmingRotate(false);
  }, [menu.open]);

  async function toggle(key: keyof ScriptFeatures) {
    if (busy) return;
    setBusy(true);
    try {
      await onChange({ ...current, [key]: !current[key] });
    } finally {
      setBusy(false);
    }
  }

  const renderPanel = (node: ReactNode) => inline ? node : createPortal(node, document.body);
  return (
    <div className="relative shrink-0">
      {!inline && (trigger ? (
        trigger({ open: menu.open, toggle: menu.toggle, ref: menu.anchorRef })
      ) : (
        <button
          type="button"
          ref={menu.anchorRef}
          onClick={menu.toggle}
          aria-expanded={menu.open}
          className="
            inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border
            font-mono text-[11px] uppercase tracking-[0.12em] font-medium
            bg-elevated border-line text-fg
            hover:border-fg/40 transition-colors duration-200
          "
        >
          <span aria-hidden>⚙</span>
          Features
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            aria-hidden
            className={`transition-transform duration-200 ${menu.open ? 'rotate-180' : ''}`}
          >
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ))}

      {(inline || (menu.open && menu.popupStyle)) && renderPanel(
        <div
          ref={menu.menuRef}
          style={inline ? undefined : menu.popupStyle ?? undefined}
          role="dialog"
          aria-label="Features"
          className="
            py-2
            bg-card border border-line rounded-sm
            shadow-flare-lg/30
            z-30 animate-rise-subtle
          "
        >
          <div className="px-3 pb-2 mb-1 border-b border-line">
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              Per-script features
            </p>
            <p className="text-faint text-[11px] mt-1">
              Control what the client can do on the review URL.
            </p>
          </div>
          <FeatureRow
            title="Comments"
            description="Clients can highlight text and leave threaded comments."
            enabled={current.comments}
            onToggle={() => toggle('comments')}
          />
          <FeatureRow
            title="Suggested edits"
            description="Clients can propose text changes that you accept or reject."
            enabled={current.suggestions}
            onToggle={() => toggle('suggestions')}
          />
          <FeatureRow
            title="Onboarding tour"
            description="Show first-visit walkthrough to clients."
            enabled={current.onboarding}
            onToggle={() => toggle('onboarding')}
          />
          <FeatureRow
            title="Allow skipping the tour"
            description="Adds a Skip link to the tour, for clients with multiple stakeholders."
            enabled={current.onboarding_skip}
            onToggle={() => toggle('onboarding_skip')}
          />

          {onOpenVersionHistory && (
            <>
              <div className="px-3 pt-2 pb-1 mt-1 border-t border-line">
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                  History
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  menu.close({ focusAnchor: true });
                  onOpenVersionHistory();
                }}
                className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-elevated transition-colors duration-150 text-left"
              >
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-medium text-fg">Version history</span>
                  <span className="block text-[11px] text-faint mt-0.5 leading-snug">
                    View and restore previous drafts.
                  </span>
                </span>
              </button>
            </>
          )}

          {onRotateSlug && (
            <>
              <div className="px-3 pt-2 pb-1 mt-1 border-t border-line">
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                  Share URL
                </p>
              </div>
              {confirmingRotate ? (
                <div className="px-3 py-2 space-y-2">
                  <p className="text-[12px] text-muted leading-snug">
                    Generates a new client URL. The old link will return 404 the moment this runs —
                    if anyone has that URL bookmarked or open, they'll need a new one.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmingRotate(false)}
                      disabled={busy}
                      className="text-[12px] font-medium text-muted hover:text-fg px-2 py-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!onRotateSlug) return;
                        setBusy(true);
                        try {
                          await onRotateSlug();
                          setConfirmingRotate(false);
                          menu.close({ focusAnchor: true });
                        } finally {
                          setBusy(false);
                        }
                      }}
                      disabled={busy}
                      className="text-[12px] font-medium text-accent hover:bg-accent/10 px-2 py-1 rounded-sm"
                    >
                      {busy ? 'Rotating…' : 'Yes, rotate URL'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingRotate(true)}
                  className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-elevated transition-colors duration-150 text-left"
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-medium text-fg">
                      Rotate client URL
                    </span>
                    <span className="block text-[11px] text-faint mt-0.5 leading-snug">
                      Generate a new shareable link. Old URL stops working.
                    </span>
                  </span>
                </button>
              )}
            </>
          )}
        </div>,
      )}
    </div>
  );
}

function FeatureRow({
  title,
  description,
  enabled,
  onToggle,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="
        w-full flex items-start gap-3 px-3 py-2.5 text-left
        transition-colors duration-150 hover:bg-elevated
      "
    >
      <Toggle on={enabled} />
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-medium text-fg">{title}</span>
        <span className="block text-[11px] text-faint mt-0.5 leading-snug">{description}</span>
      </span>
    </button>
  );
}

function Toggle({ on }: { on: boolean }) {
  return (
    <span
      className={`
        relative w-8 h-[18px] rounded-full mt-0.5 shrink-0 transition-colors duration-200
        ${on ? 'bg-accent' : 'bg-line'}
      `}
      aria-hidden
    >
      <span
        className={`
          absolute top-[2px] w-[14px] h-[14px] rounded-full bg-page
          transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]
          ${on ? 'left-[16px]' : 'left-[2px]'}
        `}
      />
    </span>
  );
}
